import { useCallback, useMemo, useState } from 'react';
import { isCarrierProjectType } from '../../../../app/navigation/courier-project-type';
import { useHasPermissions, useHasUIFeatures } from '../../../../core/permissions/has-permissions.hook';
import { Permissions } from '../../../../core/permissions/permissions.typings';
import useSearchParams from '../../../../hooks/use-search-params.hook';
import { InfiniteScrollProps } from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { useInfiniteQueryDeliveriesList } from '../../../deliveries/deliveries.queries';
import {
  deliveriesListInitialState,
  deliveriesListSearchParamOptions
} from '../../../users/details/components/user-deliveries-list/user-deliveries-list.component';
import { useQueryGetHub } from '../../../lockers/hubs.queries';
import { useQueryGetCourier, useQueryGetCourierCapabilities } from '../../couriers.queries';
import { isCourierIncomplete } from '../../logic/courier-form.map';
import { CourierDetailsActions } from '../actions/courier-details.actions';
import { useCourierStatusChange } from './use-courier-status-change.hook';

// Module-level so the reference stays stable across renders.
const INTERNAL_GROUNDFLOOR = ['internal-groundfloor'];

/**
 * A courier's details, off the one courier record. Deliberately not the users endpoint: that
 * resolves a courier's project to the carriers anchor, which their own admins cannot read.
 */
export function useCourierDetails(courierID: string, projectID: string) {
  const [currentAction, setCurrentAction] = useState<CourierDetailsActions | null>(null);

  const courier = useQueryGetCourier(courierID);

  const { activateCourier, deactivateCourier } = useCourierStatusChange(
    setCurrentAction,
    courierID,
    !!courier.data && isCourierIncomplete(courier.data)
  );

  const setAction = useCallback(
    (action: CourierDetailsActions | null) => {
      switch (action) {
        case CourierDetailsActions.ACTIVATE_COURIER:
          return activateCourier();
        case CourierDetailsActions.DEACTIVATE_COURIER:
          return deactivateCourier();
        default:
          return setCurrentAction(action);
      }
    },
    [activateCourier, deactivateCourier]
  );

  // Its own query, so the page does not wait on it and does not fail with it.
  const capabilities = useQueryGetCourierCapabilities(courierID);

  // Its own query too: the section shows the hub as unknown rather than waiting on or failing with it.
  const registrationHub = useQueryGetHub(courier.data?.registration?.hubID);

  // The actions menu gates each item with `PermissionBoundary`; the badge sits outside that, so
  // its decisions are gated here. Against the project the page was reached through, as actions are.
  const approvalPermission = useMemo(() => [{ action: Permissions.users.update, projectID }], [projectID]);
  const canDecideApproval = useHasPermissions(approvalPermission).hasAllPermissions;

  // A pool courier's carrier crumb links to the carrier's page in the network administering it,
  // not the project the page was reached through. Internal users only, who can list that project's
  // groups: the carrier pages are built for them.
  const managingProjectID = courier.data?.project?.id;
  const carriersPermission = useMemo(
    () => (managingProjectID ? [{ action: 'groups:List', projectID: managingProjectID }] : []),
    [managingProjectID]
  );
  const canListCarriers = useHasPermissions(carriersPermission).hasAllPermissions;

  // A company courier's carrier crumb links to its carrier or region project's couriers instead, for
  // viewers the navigation would offer that list to. Checked on that project, not the viewer's.
  const companyProjectID = isCarrierProjectType(courier.data?.project?.projectType)
    ? courier.data?.project?.id
    : undefined;
  const projectCouriersPermission = useMemo(
    () => (companyProjectID ? [{ action: 'users:List', projectID: companyProjectID }] : []),
    [companyProjectID]
  );
  const canOpenProjectCouriers = useHasPermissions(projectCouriersPermission).hasAllPermissions;

  const [formState, queryState, onFormInput] = useSearchParams(
    deliveriesListInitialState,
    deliveriesListSearchParamOptions
  );

  // Internal-groundfloor only, so the query follows the same gate as the table.
  const internal = useHasUIFeatures(INTERNAL_GROUNDFLOOR);

  const canOpenCarriers = internal.hasAllFeatures === true && canListCarriers === true;

  const deliveries = useInfiniteQueryDeliveriesList(
    undefined,
    1,
    { ...queryState, userID: courierID },
    internal.hasAllFeatures
  );

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: deliveries.hasNextPage || false,
    isFetchingNextPage: deliveries.isFetchingNextPage,
    fetchNextPage: deliveries.fetchNextPage,
    onScrollToBottom() {
      deliveries.fetchNextPage();
    }
  };

  return {
    courier,
    canDecideApproval,
    breadcrumbAccess: { carrierPages: canOpenCarriers, projectCouriers: canOpenProjectCouriers },
    capabilities,
    registrationHub,
    deliveries,
    deliveriesInfiniteScroll: infiniteScroll,
    formState,
    onFormInput,
    actions: { currentAction, setAction }
  } as const;
}
